// src/contexts/NotificationContext.jsx - Updated
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { notificationService } from '../services/notificationService';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    unresponded: 0,
    byCategory: {},
    byPriority: {}
  });

  const isProvider = userProfile?.role?.includes('provider') || userProfile?.role === 'provider';

  const isNotificationExpired = (notification) => {
    if (!notification.expiresAt) return false;
    
    const expiryDate = notification.expiresAt instanceof Date 
      ? notification.expiresAt 
      : new Date(notification.expiresAt);
    
    return expiryDate < new Date();
  };

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribers = [];

    // Provider Notifications Query
    if (isProvider) {
      const providerQuery = query(
        collection(db, 'pb-notifications'),
        where('providerId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const unsubProvider = onSnapshot(providerQuery, (snapshot) => {
        const providerNotifications = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            notificationType: 'provider',
            createdAt: doc.data().createdAt?.toDate?.() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
          }))
          .filter(notification => !isNotificationExpired(notification)); // Filter expired

        updateNotifications('provider', providerNotifications);
      });

      unsubscribers.push(unsubProvider);
    }

    // User Notifications Query
    const userQuery = query(
      collection(db, 'pb-notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubUser = onSnapshot(userQuery, (snapshot) => {
      const userNotifications = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          notificationType: 'user',
          createdAt: doc.data().createdAt?.toDate?.() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate?.() || new Date()
        }))
        .filter(notification => !isNotificationExpired(notification)); // Filter expired

      updateNotifications('user', userNotifications);
    });

    unsubscribers.push(unsubUser);
    setLoading(false);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [user, isProvider]);

  // Merge notifications from different sources
  const updateNotifications = (type, newNotifications) => {
    setNotifications(prev => {
      // Remove old notifications of this type
      const filtered = prev.filter(n => n.notificationType !== type);
      // Add new notifications and sort by date
      const merged = [...filtered, ...newNotifications].sort(
        (a, b) => b.createdAt - a.createdAt
      );
      return merged;
    });
  };

  // Calculate stats whenever notifications change
  useEffect(() => {
    const newStats = {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
      unresponded: notifications.filter(n => n.actionRequired && !n.responded).length,
      byCategory: notifications.reduce((acc, n) => {
        acc[n.category] = (acc[n.category] || 0) + 1;
        return acc;
      }, {}),
      byPriority: notifications.reduce((acc, n) => {
        acc[n.priority || 'normal'] = (acc[n.priority || 'normal'] || 0) + 1;
        return acc;
      }, {})
    };
    setStats(newStats);
  }, [notifications]);

  // Helper functions
  const markAsRead = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAsResponded = async (notificationId, responseData = {}) => {
    try {
      await notificationService.markAsResponded(notificationId, responseData);
    } catch (error) {
      console.error('Error marking notification as responded:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      if (unreadIds.length > 0) {
        await notificationService.markMultipleAsRead(unreadIds);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Filter helpers
  const getUnreadNotifications = () => notifications.filter(n => !n.read);
  const getActionRequiredNotifications = () => notifications.filter(n => n.actionRequired && !n.responded);
  const getNotificationsByCategory = (category) => notifications.filter(n => n.category === category);
  const getProviderNotifications = () => notifications.filter(n => n.notificationType === 'provider');
  const getUserNotifications = () => notifications.filter(n => n.notificationType === 'user');

  const value = {
    notifications,
    unreadCount: stats.unread,
    stats,
    loading,
    markAsRead,
    markAsResponded,
    markAllAsRead,
    deleteNotification,
    getUnreadNotifications,
    getActionRequiredNotifications,
    getNotificationsByCategory,
    getProviderNotifications,
    getUserNotifications,
    isProvider
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};